import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowRight, CheckCircle, FileText, Clock, Users, Award } from 'lucide-react';

const routes = [
  {
    id: 'A',
    title: 'Full Examination Pathway',
    subtitle: 'For candidates entering through direct examination',
    steps: ['Written Examination', 'Full OSCE', 'Case Log Submission', 'Viva & Case Defense', 'Certification'],
    eligibility: [
      'Valid medical registration in home jurisdiction',
      'Minimum 2 years post-licensure clinical experience',
      'Evidence of relevant training or practice in aesthetic medicine',
      'Professional references from two registered medical practitioners',
    ],
    documents: [
      'Valid medical license (certified copy)',
      'Medical qualification certificates',
      'Proof of clinical experience (employment letters)',
      'Government-issued identification',
      'Passport-sized photograph',
      'Professional references',
    ],
    minCases: 'Minimum 50 documented cases across key procedure categories',
    color: '#1B2A4A',
  },
  {
    id: 'B',
    title: 'Experienced Practitioner Pathway',
    subtitle: 'For senior practitioners with extensive documented practice',
    steps: ['Peer Endorsement', 'Audit Review', 'Modified OSCE', 'Viva & Case Defense', 'Certification'],
    eligibility: [
      'Valid medical registration in home jurisdiction',
      'Minimum 5 years of documented practice in aesthetic medicine',
      'Peer endorsement from three board-certified practitioners',
      'Published audit or clinical governance evidence',
    ],
    documents: [
      'Valid medical license (certified copy)',
      'Detailed curriculum vitae with practice history',
      'Peer endorsement letters (minimum 3)',
      'Clinical audit reports or governance documentation',
      'Case log with minimum 100 documented cases',
      'CPD evidence from the last 3 years',
    ],
    minCases: 'Minimum 100 documented cases with detailed outcome records',
    color: '#C5A572',
  },
  {
    id: 'C',
    title: 'Accredited Training Completion Pathway',
    subtitle: 'For graduates of IMCAC-recognised structured programs',
    steps: ['Program Completion Verification', 'Written Examination', 'Full OSCE', 'Case Log + Viva', 'Certification'],
    eligibility: [
      'Valid medical registration in home jurisdiction',
      'Successful completion of an IMCAC-accredited training program',
      'Program completion certificate from accredited institution',
      'Supervisor assessment and sign-off',
    ],
    documents: [
      'Valid medical license (certified copy)',
      'Program completion certificate',
      'Supervisor assessment report',
      'Training logbook (institution-verified)',
      'Government-issued identification',
      'Passport-sized photograph',
    ],
    minCases: 'As defined by the accredited training program (typically 30+ supervised cases)',
    color: '#1B2A4A',
  },
];

export default function ExaminationPathwaysPage() {
  return (
    <div data-testid="examination-pathways-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Pathways to Certification</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">Examination Pathways</h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-2xl">
            IMCAC offers three structured routes to certification, each designed to accommodate different professional backgrounds and experience levels.
          </p>
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container space-y-16">
          {routes.map((route) => (
            <Card key={route.id} className="imcac-card overflow-hidden" data-testid={`route-${route.id}-card`}>
              <div className="bg-[#1B2A4A] px-6 md:px-8 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <Badge className="bg-[#C5A572] text-white text-sm px-3 py-1">Route {route.id}</Badge>
                </div>
                <h2 className="text-2xl font-bold text-white font-heading">{route.title}</h2>
                <p className="text-sm text-slate-300 mt-1">{route.subtitle}</p>
              </div>

              <CardContent className="p-6 md:p-8">
                {/* Steps */}
                <div className="mb-8">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-[#C5A572] mb-4">Examination Pathway</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    {route.steps.map((step, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="bg-[#F1F5F9] border border-slate-200 rounded-sm px-3 py-1.5 text-sm font-medium text-[#1B2A4A]">
                          {step}
                        </span>
                        {i < route.steps.length - 1 && <ArrowRight className="h-4 w-4 text-slate-400" />}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Eligibility */}
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-widest text-[#C5A572] mb-4">
                      <Users className="h-4 w-4 inline mr-2" />Eligibility Criteria
                    </h3>
                    <ul className="space-y-2">
                      {route.eligibility.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Documents */}
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-widest text-[#C5A572] mb-4">
                      <FileText className="h-4 w-4 inline mr-2" />Required Documents
                    </h3>
                    <ul className="space-y-2">
                      {route.documents.map((doc, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                          {doc}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <Separator className="my-6" />

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Clock className="h-4 w-4 text-[#C5A572]" />
                    <span className="font-medium">Minimum Cases:</span> {route.minCases}
                  </div>
                  <Link to="/apply" data-testid={`route-${route.id}-apply`}>
                    <Button className="bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm">
                      Apply for Route {route.id} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
